import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthRightSection from '../components/AuthRightSection';
import { useAuth } from '../hooks/useAuth';
import { authApi } from '../services/api';
import { signInWithGoogle, signInWithFacebook } from '../services/firebase';
import { getApiErrorMessage } from '../utils/errors';

// Import local assets
import FacebookIcon from '../components/FacebookIcon';

/** Shared error message extraction for social auth flows (Google & Facebook). */
function getSocialAuthErrorMessage(err: any, provider: string): string {
  if (err.code === 'auth/popup-closed-by-user') return 'Sign-in was cancelled. Please try again.';
  if (err.code === 'auth/popup-blocked')
    return 'Popup was blocked. Please allow popups for this site.';
  if (err.message === 'Network Error' || err.code === 'ERR_NETWORK')
    return 'Unable to connect to server. Please check your internet connection and try again.';
  if (err.response?.status === 0 || err.message?.includes('ECONNREFUSED'))
    return 'Backend server is not running. Please start the backend service.';

  // Handle validation errors
  if (err.response?.data?.detail) {
    if (typeof err.response.data.detail === 'string') return err.response.data.detail;
    if (Array.isArray(err.response.data.detail))
      return err.response.data.detail[0]?.msg || 'Validation error';
  }
  return err.message || `${provider} sign-in failed`;
}

const SignIn: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<string | null>(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleGoogleSignIn = async () => {
    setSocialLoading('google');
    setError('');

    try {
      const { token } = await signInWithGoogle();
      const response = await authApi.googleLogin(token);
      await login(response.access_token);
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Google Sign In Error:', err);
      setError(getSocialAuthErrorMessage(err, 'Google'));
    } finally {
      setSocialLoading(null);
    }
  };

  const handleFacebookSignIn = async () => {
    setSocialLoading('facebook');
    setError('');

    try {
      const { token } = await signInWithFacebook();
      const response = await authApi.facebookLogin(token);
      await login(response.access_token);

      // Warn user if they signed in without a real email (shown as a banner on the dashboard)
      navigate('/dashboard', {
        replace: true,
        state: response.requires_email_update
          ? {
              notice:
                'Please add a real email address in Settings to receive notifications and enable password recovery.',
            }
          : undefined,
      });
    } catch (err: any) {
      console.error('Facebook Sign In Error:', err);
      setError(getSocialAuthErrorMessage(err, 'Facebook'));
    } finally {
      setSocialLoading(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Call the login API
      const response = await authApi.login({
        username: email,
        password: password,
      });

      // Store the token and update auth context
      await login(response.access_token);

      // Navigate to dashboard with replace to prevent going back to login
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      setError(
        err.response
          ? getApiErrorMessage(err, 'Invalid email or password')
          : err.message || 'Unable to reach the server. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="bg-[#ffffff] relative w-full min-h-screen flex flex-col lg:flex-row"
      data-name="2.1. Sign in - step 1"
      id="node-12_7221"
    >
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div
          className="flex flex-col gap-8 sm:gap-12 lg:gap-20 items-center justify-center max-w-[673px] w-full"
          id="node-12_7238"
        >
          <div className="flex flex-col gap-8 items-start justify-start w-full" id="node-12_7239">
            <div className="flex flex-col gap-0.5 items-start justify-center" id="node-12_7240">
              <div
                className="font-['Ovo',_serif] text-[#000000] text-[24px] sm:text-[28px] lg:text-[32px] text-center uppercase"
                id="node-12_7241"
              >
                <p className="block leading-[normal]">LOGIN TO YOUR CLOUT ACCOUNT</p>
              </div>
              <div className="flex flex-row gap-0.5 items-start justify-center" id="node-12_7242">
                <div className="p-[2px]" data-name="Have an account login" id="node-12_7243">
                  <div
                    className="font-['Ovo',_serif] font-normal text-[#000000] text-[16px]"
                    id="node-I12_7243-2_3"
                  >
                    New here?
                  </div>
                </div>
                <Link
                  to="/signup"
                  className="font-['Ovo',_serif] font-semibold text-[#c6a664] text-[16px] underline"
                  id="node-12_7244"
                >
                  Register
                </Link>
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* Form fields */}
            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-8 items-center justify-center w-full"
              id="node-12_7245"
            >
              <div className="flex flex-col gap-1 w-full" data-name="Text field" id="node-12_7246">
                <label
                  className="font-['Ovo',_serif] font-normal text-[#1f1f1f] text-[16px]"
                  id="node-I12_7246-2_17"
                >
                  Email Address/Phone Number
                </label>
                <input
                  type="text"
                  placeholder="Enter your registered email / Phone Number"
                  className="h-14 w-full border border-[#eaeaea] px-6 py-4 font-['Ovo',_serif] text-[16px] text-[#1f1f1f] placeholder:text-[#767472]"
                  id="node-I12_7246-2_18"
                  data-testid="email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>

              <div className="flex flex-col gap-1 w-full" id="node-12_7247">
                <div
                  className="flex flex-col gap-1 w-full"
                  data-name="Text field"
                  id="node-12_7248"
                >
                  <label
                    className="font-['Ovo',_serif] font-normal text-[#1f1f1f] text-[16px]"
                    id="node-12_7250"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      className="h-14 w-full border border-[#eaeaea] px-6 py-4 pr-12 font-['Ovo',_serif] text-[16px] text-[#1f1f1f] placeholder:text-[#767472]"
                      id="node-12_7254"
                      data-testid="password-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-5 top-1/2 transform -translate-y-1/2"
                      data-name="Password hide / see"
                      id="node-12_7259"
                    >
                      <svg
                        className="w-6 h-6"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z"
                          stroke="#1f1f1f"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                          stroke="#1f1f1f"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Link
                    to="/forgot-password"
                    className="font-['Inter',_serif] font-normal text-[#1f1f1f] text-[16px] underline"
                    id="node-12_7262"
                  >
                    Forget your password
                  </Link>
                </div>
              </div>

              <button
                type="submit"
                className="border border-[#1f1f1f] border-solid px-6 py-3 font-['Ovo',_serif] text-[#000000] text-[20px] uppercase bg-transparent hover:bg-[#1f1f1f] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                id="node-12_7263"
                data-testid="signin-button"
                disabled={loading}
              >
                {loading ? 'SIGNING IN...' : 'SIGN IN'}
              </button>
            </form>
          </div>

          <div className="flex flex-col gap-6 items-center justify-start w-full" id="node-12_7264">
            <div
              className="flex flex-row gap-[23px] items-center justify-center w-full"
              data-name="Divider"
              id="node-12_7265"
            >
              <div
                className="bg-[#eaeaea] h-0.5 flex-1"
                data-name="Divider"
                id="node-I12_7265-6_1758"
              />
              <div
                className="font-['Ovo',_serif] text-[#c6a664] text-[24px] uppercase"
                id="node-I12_7265-6_1759"
              >
                OR
              </div>
              <div
                className="bg-[#eaeaea] h-0.5 flex-1"
                data-name="Divider"
                id="node-I12_7265-6_1760"
              />
            </div>
            <div
              className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-start justify-center w-full"
              id="node-12_7266"
            >
              <button
                onClick={handleFacebookSignIn}
                disabled={loading || socialLoading !== null}
                className="bg-[#ffffff] h-[60px] sm:h-[72px] relative rounded-lg flex-1 sm:max-w-[329px] border border-[#eaeaea] hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-name="Social media signup/login"
                id="node-12_7267"
              >
                <div className="flex flex-row gap-2 items-center justify-center h-full">
                  <div className="relative size-8 flex items-center justify-center">
                    <FacebookIcon />
                  </div>
                  <div className="font-['Ovo',_serif] text-[#1f1f1f] text-[16px] sm:text-[18px] lg:text-[20px] uppercase">
                    {socialLoading === 'facebook' ? 'Signing in...' : 'Sign In with Facebook'}
                  </div>
                </div>
              </button>
              <button
                onClick={handleGoogleSignIn}
                disabled={loading || socialLoading !== null}
                className="bg-[#ffffff] h-[60px] sm:h-[72px] relative rounded-lg flex-1 sm:max-w-[329px] border border-[#eaeaea] hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-name="Social media signup/login"
                id="node-12_7268"
              >
                <div className="flex flex-row gap-2 items-center justify-center h-full">
                  <div className="relative size-6 flex items-center justify-center">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                  </div>
                  <div className="font-['Ovo',_serif] text-[#1f1f1f] text-[16px] sm:text-[18px] lg:text-[20px] uppercase">
                    {socialLoading === 'google' ? 'Signing in...' : 'Sign In with Google'}
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Mobile-only benefits section */}
          <div className="block md:hidden w-full bg-[#f8f6f2] p-6 rounded-lg">
            <div className="text-center mb-4">
              <div className="font-['Ovo',_serif] text-[#000000] text-[20px] uppercase">
                Get ready to tout your CLOUT!
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#c6a664] rounded-full mt-2 flex-shrink-0"></div>
                <div className="font-['Ovo',_serif] text-[#000000] text-[14px]">
                  Discover your initial CLOUT score
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#c6a664] rounded-full mt-2 flex-shrink-0"></div>
                <div className="font-['Ovo',_serif] text-[#000000] text-[14px]">
                  Complete your profile to become CLOUT certified
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#c6a664] rounded-full mt-2 flex-shrink-0"></div>
                <div className="font-['Ovo',_serif] text-[#000000] text-[14px]">
                  Invite peers to endorse you and build your CLOUT!
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Illustration */}
      <div className="hidden md:block flex-1 min-h-[400px] lg:min-h-screen">
        <AuthRightSection />
      </div>
    </div>
  );
};

export default SignIn;
